import { useState, useEffect, useCallback } from 'react';
import { NotificationItem } from '../types/notifications';
import { notificationHistoryService } from '../services/notificationHistoryService';

export function useNotificationHistory() {
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load notifications from backend API
  const loadNotifications = useCallback(async () => {
    try {
      const allNotifications = await notificationHistoryService.fetchNotificationsFromBackend();
      setNotifications(allNotifications);
      setUnreadCount(allNotifications.filter(n => !n.isRead).length);
      setError(null);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load notifications';
      setError(errorMessage);
      console.error('Failed to load notifications:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Add a new notification
  const addNotification = useCallback(async (notification: Omit<NotificationItem, 'id' | 'createdAt' | 'isRead'>) => {
    try {
      await notificationHistoryService.addNotification(notification);
      loadNotifications(); // Refresh the list
      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to add notification';
      setError(errorMessage);
      console.error('Failed to add notification:', err);
      return false;
    }
  }, [loadNotifications]);

  // Mark notification as read
  const markAsRead = useCallback(async (notificationId: string) => {
    try {
      await notificationHistoryService.markAsRead(notificationId);
      loadNotifications(); // Refresh the list
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to mark notification as read';
      setError(errorMessage);
      console.error('Failed to mark notification as read:', err);
    }
  }, [loadNotifications]);

  // Mark all notifications as read
  const markAllAsRead = useCallback(async () => {
    try {
      await notificationHistoryService.markAllAsRead();
      loadNotifications(); // Refresh the list
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to mark all notifications as read';
      setError(errorMessage);
      console.error('Failed to mark all notifications as read:', err);
    }
  }, [loadNotifications]);

  // Delete notification
  const deleteNotification = useCallback(async (notificationId: string) => {
    try {
      await notificationHistoryService.deleteNotification(notificationId);
      loadNotifications(); // Refresh the list
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete notification';
      setError(errorMessage);
      console.error('Failed to delete notification:', err);
    }
  }, [loadNotifications]);

  // Clear all notifications
  const clearAllNotifications = useCallback(async () => {
    try {
      await notificationHistoryService.clearAllNotifications();
      loadNotifications(); // Refresh the list
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to clear notifications';
      setError(errorMessage);
      console.error('Failed to clear notifications:', err);
    }
  }, [loadNotifications]);

  // Simulate receiving notifications (for demo)
  const simulateNotifications = useCallback(async () => {
    try {
      await notificationHistoryService.simulateNotifications();
      // Refresh after a delay to allow notifications to be added
      setTimeout(loadNotifications, 3000);
    } catch (err) {
      console.error('Failed to simulate notifications:', err);
    }
  }, [loadNotifications]);

  // Load notifications on mount and poll every 30 seconds
  useEffect(() => {
    loadNotifications();

    // Poll for new notifications every 30 seconds
    const interval = setInterval(loadNotifications, 30 * 1000); // 30 seconds
    return () => clearInterval(interval);
  }, [loadNotifications]);

  return {
    notifications,
    unreadCount,
    isLoading,
    error,
    addNotification,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    clearAllNotifications,
    simulateNotifications,
    refresh: loadNotifications,
  };
}